/**
 * BIP-39 助记词：生成 / 校验 / → seed。
 *
 * 口径（ARCH §8.3，全站唯一）：
 *  - 词表：官方 BIP-39 英文 2048 词（见 `./wordlist.ts`）
 *  - seed：`PBKDF2-HMAC-SHA512(2048, salt = "mnemonic" + passphrase)`
 *  - 助记词**仅内存**，永不明文落盘（seal 进 keystore 后即为密文）
 */

import { randomBytes, hashSha256 } from './noble';
import { BIP39_WORDLIST, BIP39_WORD_INDEX } from './wordlist';
import { MeshLinkError } from '@/lib/result';

export type MnemonicStrength = 128 | 160 | 192 | 224 | 256;

const MNEMONIC_SALT_PREFIX = 'mnemonic';
const PBKDF2_ITERATIONS = 2048;
const SEED_BYTES = 64;

const encoder = new TextEncoder();

/** 助记词词数 → 熵位数。 */
export function wordsToStrength(wordCount: number): MnemonicStrength {
  switch (wordCount) {
    case 12:
      return 128;
    case 15:
      return 160;
    case 18:
      return 192;
    case 21:
      return 224;
    case 24:
      return 256;
    default:
      throw new MeshLinkError('E_MNEMONIC_INVALID', `不支持的助记词长度: ${wordCount}`);
  }
}

/** 生成 12 词助记词（默认 128 位熵）。 */
export function generateMnemonic(strength: MnemonicStrength = 128): string[] {
  const entropy = randomBytes(strength / 8);
  return entropyToMnemonic(entropy);
}

/** 熵 → 助记词（含 BIP-39 校验和）。 */
export function entropyToMnemonic(entropy: Uint8Array): string[] {
  const entropyBits = entropy.length * 8;
  if (![128, 160, 192, 224, 256].includes(entropyBits)) {
    throw new MeshLinkError('E_MNEMONIC_INVALID', `熵长度必须为 16/20/24/28/32 字节，当前 ${entropy.length}`);
  }
  const checksumBits = entropyBits / 32;
  const checksum = hashSha256(entropy);

  // entropy ‖ checksum(前 checksumBits 位)
  const totalBits = entropyBits + checksumBits;
  const bits: boolean[] = [];
  for (const byte of entropy) {
    for (let i = 7; i >= 0; i -= 1) bits.push(((byte >> i) & 1) === 1);
  }
  for (let i = 0; i < checksumBits; i += 1) {
    const byteIndex = Math.floor(i / 8);
    const bitIndex = 7 - (i % 8);
    bits.push(((checksum[byteIndex] >> bitIndex) & 1) === 1);
  }

  const words: string[] = [];
  for (let i = 0; i < totalBits / 11; i += 1) {
    let index = 0;
    for (let j = 0; j < 11; j += 1) {
      index = (index << 1) | (bits[i * 11 + j] ? 1 : 0);
    }
    words.push(BIP39_WORDLIST[index]);
  }
  return words;
}

/** 助记词 → 熵（含校验和验证）。校验失败抛 E_MNEMONIC_INVALID 语义错误。 */
export function mnemonicToEntropy(words: string[]): Uint8Array {
  const normalized = words.map((w) => w.trim().toLowerCase()).filter(Boolean);
  const entropyBits = wordsToStrength(normalized.length) as number;
  const checksumBits = entropyBits / 32;

  const bits: boolean[] = [];
  for (const word of normalized) {
    const index = BIP39_WORD_INDEX.get(word);
    if (index === undefined) throw new MeshLinkError('E_MNEMONIC_INVALID', `助记词包含无效单词: ${word}`);
    for (let i = 10; i >= 0; i -= 1) bits.push(((index >> i) & 1) === 1);
  }

  const entropy = new Uint8Array(entropyBits / 8);
  for (let i = 0; i < entropy.length; i += 1) {
    let byte = 0;
    for (let j = 0; j < 8; j += 1) byte = (byte << 1) | (bits[i * 8 + j] ? 1 : 0);
    entropy[i] = byte;
  }

  // 校验和比对
  const checksum = hashSha256(entropy);
  for (let i = 0; i < checksumBits; i += 1) {
    const byteIndex = Math.floor(i / 8);
    const bitIndex = 7 - (i % 8);
    const expected = ((checksum[byteIndex] >> bitIndex) & 1) === 1;
    if (bits[entropyBits + i] !== expected) throw new MeshLinkError('E_MNEMONIC_INVALID', '助记词校验和不匹配');
  }
  return entropy;
}

/** 校验助记词是否合法（词表 + 校验和）。 */
export function validateMnemonic(words: string[] | string): boolean {
  const list = typeof words === 'string' ? words.trim().toLowerCase().split(/\s+/).filter(Boolean) : words;
  try {
    mnemonicToEntropy(list);
    return true;
  } catch {
    return false;
  }
}

/** 返回不认识的单词（UI 逐词标红用）。 */
export function unknownWords(words: string[]): string[] {
  return words.map((w) => w.trim().toLowerCase()).filter((w) => w.length > 0 && !BIP39_WORD_INDEX.has(w));
}

/**
 * 助记词 → 64 字节 seed（BIP-39 标准）。
 * @param words 12/15/18/21/24 词
 * @param passphrase 可选口令（BIP-39 第 13 词概念，MVP 默认空串）
 */
export async function mnemonicToSeed(words: string[], passphrase = ''): Promise<Uint8Array> {
  const normalized = words.map((w) => w.trim().toLowerCase()).filter(Boolean);
  if (!validateMnemonic(normalized)) throw new MeshLinkError('E_MNEMONIC_INVALID');

  const password = encoder.encode(normalized.join(' '));
  const salt = encoder.encode(MNEMONIC_SALT_PREFIX + passphrase);

  const key = await crypto.subtle.importKey('raw', password, 'PBKDF2', false, ['deriveBits']);
  const derived = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-512' },
    key,
    SEED_BYTES * 8,
  );
  return new Uint8Array(derived);
}
